<div class="card mb-4">
  <div class="card-header p-3 d-flex justify-content-between align-items-center">
    <h5 class="mb-0"><i class="bi bi-broadcast me-2"></i>DJI Connection</h5>
    <span *ngIf="!loading() && status() as s" class="badge" [ngClass]="s.state === 'receiving' ? 'bg-success' : 'bg-secondary'">
      {{ s.state === 'receiving' ? 'Receiving DJI data' : 'No DJI data yet' }}
    </span>
  </div>

  <div class="card-body">
    <div *ngIf="loading()" class="text-muted small">
      <span class="spinner-border spinner-border-sm me-2" aria-hidden="true"></span>Checking DJI connection…
    </div>

    <p *ngIf="!loading() && failed()" class="text-muted small mb-0">
      DJI connection status is unavailable right now. Please try again later.
    </p>

    <ng-container *ngIf="!loading() && status() as s">
      <p *ngIf="s.state === 'receiving'" class="mb-0">
        {{ s.flightCount }} {{ s.flightCount === 1 ? 'flight' : 'flights' }} received
        <ng-container *ngIf="s.lastFlightAt"> · last flight {{ s.lastFlightAt | date: 'MMM d, y' }}</ng-container>
      </p>

      <p *ngIf="s.state === 'receiving'" class="small text-muted mt-1 mb-0">
        <ng-container *ngIf="s.linkedCount === s.flightCount">All linked to a spray record.</ng-container>
        <ng-container *ngIf="s.linkedCount !== s.flightCount">
          {{ s.linkedCount }} linked to a spray record<ng-container *ngIf="notLinkedCount(s) > 0">, {{ notLinkedCount(s) }} not linked yet</ng-container><ng-container *ngIf="s.dismissedCount > 0">, {{ s.dismissedCount }} dismissed</ng-container>.
        </ng-container>
      </p>

      <div *ngIf="s.recentFlights.length > 0" class="table-responsive mt-3">
        <table class="table table-sm align-middle mb-0">
          <thead>
            <tr class="small text-muted">
              <th scope="col">Flight</th>
              <th scope="col">Area</th>
              <th scope="col">Duration</th>
              <th scope="col">Spray record</th>
            </tr>
          </thead>
          <tbody>
            <tr *ngFor="let flight of s.recentFlights; trackBy: trackByIntake">
              <td class="text-nowrap">
                <ng-container *ngIf="flight.flightStartedAt; else unknownDate">{{ startLabel(flight) }}</ng-container>
                <ng-template #unknownDate><span class="text-muted">Date unknown</span></ng-template>
              </td>
              <td class="text-nowrap">{{ acres(flight.workAreaAcres) }}</td>
              <td class="text-nowrap">{{ duration(flight.durationSeconds) }}</td>
              <td class="text-nowrap">
                <ng-container *ngIf="flight.sprayRecordId; else notLinked">
                  <span *ngIf="flight.sprayRecordStatus" class="badge me-2" [ngClass]="flight.sprayRecordStatus | statusBadge">{{ recordStatusLabel(flight.sprayRecordStatus) }}</span>
                  <a *ngIf="flight.sprayRecordStatus === 'draft'; else viewRecord" [routerLink]="['/logbook/spray-records', flight.sprayRecordId, 'edit']">Finish draft</a>
                  <ng-template #viewRecord><a [routerLink]="['/logbook/spray-records', flight.sprayRecordId, 'view']">View spray record</a></ng-template>
                </ng-container>
                <ng-template #notLinked>
                  <ng-container *ngIf="flight.linkStatus !== 'dismissed'; else dismissed">
                    <button type="button" class="btn btn-sm btn-outline-primary me-2" [disabled]="busyIntakeId() !== null" (click)="showPreview(flight)">
                      <span *ngIf="busyIntakeId() === flight.intakeId && !preview()" class="spinner-border spinner-border-sm me-1" aria-hidden="true"></span>Create spray record
                    </button>
                    <button type="button" class="btn btn-sm btn-link text-muted p-0" [disabled]="busyIntakeId() !== null" (click)="dismiss(flight)">Dismiss</button>
                  </ng-container>
                  <ng-template #dismissed>
                    <span class="badge bg-secondary me-2">Dismissed</span>
                    <button type="button" class="btn btn-sm btn-link p-0" [disabled]="busyIntakeId() !== null" (click)="restore(flight)">Restore</button>
                  </ng-template>
                </ng-template>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      <div *ngIf="preview() as draft" class="mt-3 p-3 rounded border bg-canvas">
        <div class="fw-medium mb-2"><i class="bi bi-file-earmark-plus me-2"></i>This will create a draft spray record</div>
        <ng-container *ngIf="describeDraft(draft) as d">
          <dl class="row small mb-2">
            <dt class="col-sm-4 text-muted fw-normal">Date</dt><dd class="col-sm-8 mb-1">{{ d.date }}</dd>
            <dt class="col-sm-4 text-muted fw-normal">Aircraft</dt><dd class="col-sm-8 mb-1">{{ droneName || 'This drone' }}</dd>
            <dt class="col-sm-4 text-muted fw-normal">Pilot</dt><dd class="col-sm-8 mb-1">{{ draft.pilotName || 'You' }} <span class="text-muted">(you can change this)</span></dd>
            <dt class="col-sm-4 text-muted fw-normal">Times</dt><dd class="col-sm-8 mb-1">{{ d.time }}</dd>
            <dt class="col-sm-4 text-muted fw-normal">Acres sprayed</dt><dd class="col-sm-8 mb-1">{{ d.acres }}</dd>
            <dt class="col-sm-4 text-muted fw-normal">Number of flights</dt><dd class="col-sm-8 mb-1">{{ d.flights }}</dd>
            <dt class="col-sm-4 text-muted fw-normal">Speed</dt><dd class="col-sm-8 mb-1">{{ d.speed }}</dd>
          </dl>
        </ng-container>
        <p class="small text-muted mb-3">
          You'll add the field, customer and chemicals next. DJI doesn't know those. The flight is linked to the record right away.
        </p>
        <button type="button" class="btn btn-sm btn-primary me-2" [disabled]="busyIntakeId() !== null" (click)="createSprayRecord({ intakeId: previewIntakeId()! })">
          <span *ngIf="busyIntakeId() !== null" class="spinner-border spinner-border-sm me-1" aria-hidden="true"></span>Create draft and open it
        </button>
        <button type="button" class="btn btn-sm btn-link text-muted" [disabled]="busyIntakeId() !== null" (click)="cancelPreview()">Cancel</button>
      </div>

      <p *ngIf="actionError()" class="small text-danger mt-2 mb-0" role="alert">{{ actionError() }}</p>
      <p *ngIf="notLinkedCount(s) > 0" class="small text-muted mt-2 mb-0">
        <em>Create spray record</em> shows what it would start from what DJI recorded, before anything is created. Dismiss flights that were tests or practice.
      </p>
      <p *ngIf="s.flightCount > s.recentFlights.length" class="small text-muted mt-2 mb-0">
        Showing the latest {{ s.recentFlights.length }} of {{ s.flightCount }} flights.
      </p>

      <p *ngIf="s.state === 'no_data'" class="mb-0 text-muted">
        ADN hasn't received any DJI flights for this drone. If you haven't connected it yet, follow the steps below.
      </p>

      <div class="mt-3">
        <button type="button" class="btn btn-link p-0 small" (click)="toggleGuide()" [attr.aria-expanded]="showGuide()">
          <i class="bi me-1" [ngClass]="showGuide() ? 'bi-chevron-down' : 'bi-chevron-right'"></i>How to connect a DJI drone
        </button>
        <div *ngIf="showGuide()" class="mt-3">
          <app-dji-authorization-guide></app-dji-authorization-guide>
        </div>
      </div>
    </ng-container>
  </div>
</div>
